import { h } from 'preact'
import { useEffect, useState, useRef } from 'preact/hooks'
import { useSelector } from 'react-redux'
import { lazy, Suspense } from 'preact/compat'
import { pagingFilteringContextSelector } from '@core/state/page/pageSelectors'

import modelTypes from '@constants/modelTypes'
import translate from '@core/translate'
import useSSR from '@core/hooks/useSSR'
import FacetContainer from '@features/elastic/facets/FacetContainer'
import PageSizeFacet from '../components/PageSizeFacet'
import SortFacet from '../components/SortFacet'

const RangeSlider = lazy(() => import('@features/elastic/rangeSlider/RangeSlider'))

export default function FacetList({ facets, facetsOpen }) {
	const { catalogSettings } = useSSR([modelTypes.catalogSettings, modelTypes.search])

	const [currentFacets, setFacets] = useState(facets)
	const [priceOpen, setPriceOpen] = useState(false)
	const { minPrice } = useSelector(state => state.currentPage.priceStats)
	const { pageSizeOptions, availableSortOptions } = useSelector(pagingFilteringContextSelector)

	const showPriceRangeSlider = minPrice !== null && !!catalogSettings.enablePriceRangeFiltering
	const showPageSizeOptions = pageSizeOptions && pageSizeOptions.length > 1
	const showSortOptions = availableSortOptions && availableSortOptions.length > 1

	useEffect(() => {
		setFacets(facets)
	}, [facets])

	const PriceRangeFacet = () => (
		<div className={`facets__facet ${priceOpen ? '--active' : ''}`}>
			<div
				className="facets__label"
				onClick={() => setPriceOpen(!priceOpen)}
				tabIndex={0}
				role="button"
			>
				<span>{translate('search.priceRange')}</span>

				<svg
					width="16"
					height="16"
					viewBox="0 0 48 48"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M8.36612 16.1161C7.87796 16.6043 7.87796 17.3957 8.36612 17.8839L23.1161 32.6339C23.6043 33.122 24.3957 33.122 24.8839 32.6339L39.6339 17.8839C40.122 17.3957 40.122 16.6043 39.6339 16.1161C39.1457 15.628 38.3543 15.628 37.8661 16.1161L24 29.9822L10.1339 16.1161C9.64573 15.628 8.85427 15.628 8.36612 16.1161Z"
						fill="#212121"
					/>
				</svg>
			</div>
			{priceOpen && (
				<div className="facets__facet-options">
					<Suspense
						fallback={
							<div className="is-rotating flex align-center justify-center pad-3">
								<svg
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M16.0518 5.0285C15.7169 5.46765 15.8013 6.09515 16.2405 6.43007C17.9675 7.74714 19 9.78703 19 12C19 15.4973 16.4352 18.3956 13.084 18.9166L13.7929 18.2071C14.1834 17.8166 14.1834 17.1834 13.7929 16.7929C13.4024 16.4024 12.7692 16.4024 12.3787 16.7929L9.87868 19.2929C9.48816 19.6834 9.48816 20.3166 9.87868 20.7071L12.3787 23.2071C12.7692 23.5976 13.4024 23.5976 13.7929 23.2071C14.1834 22.8166 14.1834 22.1834 13.7929 21.7929L12.9497 20.9505C17.4739 20.476 21 16.6498 21 12C21 9.15644 19.6712 6.53122 17.4533 4.83978C17.0142 4.50486 16.3867 4.58936 16.0518 5.0285ZM14.1213 3.29289L11.6213 0.792893C11.2308 0.402369 10.5976 0.402369 10.2071 0.792893C9.84662 1.15338 9.81889 1.72061 10.1239 2.1129L10.2071 2.20711L11.0503 3.04951C6.52615 3.52399 3 7.35021 3 12C3 14.7198 4.21515 17.2432 6.2716 18.9419C6.6974 19.2936 7.32771 19.2335 7.67943 18.8077C8.03116 18.3819 7.97111 17.7516 7.54531 17.3999C5.94404 16.0772 5 14.1168 5 12C5 8.50269 7.56475 5.60441 10.916 5.08343L10.2071 5.79289C9.81658 6.18342 9.81658 6.81658 10.2071 7.20711C10.5676 7.56759 11.1348 7.59532 11.5271 7.2903L11.6213 7.20711L14.1213 4.70711C14.4818 4.34662 14.5095 3.77939 14.2045 3.3871L14.1213 3.29289Z"
										fill="#212121"
									/>
								</svg>
							</div>
						}
					>
						<RangeSlider />
					</Suspense>
				</div>
			)}
		</div>
	)

	return (
		<div className="facets">
			{facets && (
				<ul className="facets__list">
					{showPriceRangeSlider && <PriceRangeFacet />}
					{showPageSizeOptions && <PageSizeFacet pageSizeOptions={pageSizeOptions} />}
					{showSortOptions && <SortFacet availableSortOptions={availableSortOptions} />}
					{currentFacets.map(facet => (
						<FacetContainer
							facetKey={facet.key}
							key={facet.name}
							field={facet.name}
							options={facet.options}
						/>
					))}
				</ul>
			)}
		</div>
	)
}
